import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight, Shield, Clock, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import heroImage from "@/assets/blog-asylum-process.jpg";
import { generateArticleSchema } from "@/lib/seo-schemas";

const AsylumProcess2025 = () => {
  const schema = generateArticleSchema(
    "Understanding the Asylum Process in 2025",
    "A comprehensive guide to seeking asylum in the United States, including recent changes and what to expect in 2025.",
    "2025-01-15",
    "2025-01-15",
    "https://danielalbertlaw.com/blog/understanding-asylum-process-2025"
  );

  return (
    <div className="min-h-screen">
      <SEO
        title="Understanding the Asylum Process in 2025 | Daniel Albert Law"
        description="A comprehensive guide to seeking asylum in the United States, including recent changes and what to expect in 2025. Expert immigration attorney advice."
        keywords="asylum process, asylum 2025, seeking asylum, immigration attorney, Houston asylum lawyer"
        schema={schema}
      />
      
      <Header />
      
      <Breadcrumbs
        items={[
          { label: "Blog", href: "/blog" },
          { label: "Understanding the Asylum Process in 2025" }
        ]}
      />

      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl text-primary-foreground">
            <div className="flex items-center gap-4 mb-4 text-sm">
              <span className="inline-block bg-accent/20 text-accent px-3 py-1 rounded-full font-semibold">
                Immigration
              </span>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>January 15, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Daniel Albert</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg">
              Understanding the Asylum Process in 2025
            </h1>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-xl text-muted-foreground leading-relaxed">
                Seeking asylum in the United States is a critical legal process for individuals fleeing persecution in their home countries. As we enter 2025, understanding the current asylum process, recent changes, and what to expect is essential for anyone considering applying for asylum protection.
              </p>
            </div>

            {/* Key Points */}
            <Card className="mb-12 bg-gradient-subtle">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <Shield className="h-6 w-6 text-accent" />
                  Key Takeaways
                </h2>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>You must apply for asylum within one year of arriving in the United States</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Recent policy changes have streamlined certain application procedures</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Having legal representation significantly increases your chances of success</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>The process can take several months to years depending on your case</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Main Content Sections */}
            <div className="space-y-12">
              <section>
                <h2 className="text-3xl font-bold mb-6">What is Asylum?</h2>
                <p className="text-lg text-muted-foreground mb-4">
                  Asylum is a form of protection granted to individuals who have suffered persecution or fear persecution in their home country due to their race, religion, nationality, membership in a particular social group, or political opinion. It allows you to remain in the United States and eventually apply for a green card.
                </p>
                <p className="text-lg text-muted-foreground">
                  Unlike refugee status, which is applied for from outside the United States, asylum is requested by individuals who are already in the U.S. or at a port of entry.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Recent Changes in 2025</h2>
                <p className="text-lg text-muted-foreground mb-4">
                  The asylum process has undergone several important updates in 2025:
                </p>
                <ul className="space-y-4 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <Clock className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>Expedited Processing:</strong> Certain asylum cases now qualify for expedited processing, reducing wait times from years to months in eligible situations.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <FileText className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>Enhanced Documentation Requirements:</strong> New guidelines require more detailed country condition reports and supporting evidence.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>Improved Legal Access:</strong> Expanded legal orientation programs are now available to asylum seekers at major processing centers.
                    </div>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">The Asylum Application Process</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-semibold mb-3">Step 1: Filing Your Application</h3>
                    <p className="text-lg text-muted-foreground">
                      You must file Form I-589 (Application for Asylum and for Withholding of Removal) within one year of your arrival in the United States, unless you qualify for an exception. The application requires detailed information about your identity, travel history, and the persecution you fear or have experienced.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold mb-3">Step 2: Biometrics and Interview</h3>
                    <p className="text-lg text-muted-foreground">
                      After filing, you'll receive a notice to appear for biometrics collection. Subsequently, you'll be scheduled for an asylum interview with a USCIS asylum officer or, in some cases, a hearing before an immigration judge.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold mb-3">Step 3: Decision</h3>
                    <p className="text-lg text-muted-foreground">
                      The asylum officer or judge will review your case and make a decision. If granted, you can remain in the U.S., work legally, and after one year, apply for a green card. If denied, you may have appeal options depending on your situation.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Why You Need an Immigration Attorney</h2>
                <p className="text-lg text-muted-foreground mb-4">
                  Statistics show that asylum seekers with legal representation are significantly more likely to be granted asylum than those without. An experienced immigration attorney can:
                </p>
                <ul className="space-y-3 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Help you gather and organize the necessary evidence to support your claim</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Prepare you for your asylum interview or hearing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Identify the strongest legal arguments for your case</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Navigate complex procedural requirements and deadlines</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Represent you in appeals if your initial application is denied</span>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Common Challenges in Asylum Cases</h2>
                <p className="text-lg text-muted-foreground mb-4">
                  Asylum cases can be complex, and applicants often face several challenges:
                </p>
                <ul className="space-y-3 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span><strong>Burden of Proof:</strong> You must provide credible evidence of persecution or well-founded fear of persecution</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span><strong>One-Year Filing Deadline:</strong> Missing this deadline can bar your asylum claim unless you qualify for an exception</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span><strong>Credibility Issues:</strong> Any inconsistencies in your testimony can negatively impact your case</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span><strong>Changing Country Conditions:</strong> Updates to conditions in your home country can affect your eligibility</span>
                  </li>
                </ul>
              </section>
            </div>

            {/* Related Services */}
            <Card className="mt-12 bg-primary text-primary-foreground">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-4">Need Help with Your Asylum Case?</h2>
                <p className="text-lg mb-6 opacity-95">
                  At Daniel Albert Law Firm, we have extensive experience helping asylum seekers navigate the complex immigration system. Our trilingual team can provide expert guidance in English, Spanish, and Vietnamese.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="outline" className="border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                    <Link to="/practice-areas/immigration-services/asylum-in-the-united-states">
                      Learn More About Asylum Services
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </article>

      <CTASection
        title="Have Questions About Asylum?"
        description="Contact us today. Our experienced immigration attorneys are ready to help you with your asylum case."
      />

      <Footer />
    </div>
  );
};

export default AsylumProcess2025;